'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** The three sections of the single-page membership application. */
export type StepId = 'info' | 'shipping' | 'billing';

export type StepStatus = 'active' | 'complete';

/** Ordered — each section's Continue advances to the next one. */
export const APPLICATION_STEPS: StepId[] = ['info', 'shipping', 'billing'];

type StepStatusMap = Record<StepId, StepStatus>;

export type ApplicationStepsTools = {
  /** Current status of every step. */
  status: StepStatusMap;
  /** Whether a step is currently expanded. */
  isOpen: (step: StepId) => boolean;
  /** Expand or collapse a section. Opening one closes the others. */
  requestOpen: (step: StepId, open: boolean) => void;
  /** True while any section is expanded. */
  anyOpen: boolean;
  /**
   * Report whether a step's required fields currently all validate. Drives the
   * complete/active styling and recaps only — it never collapses anything.
   */
  reportComplete: (step: StepId, complete: boolean) => void;
  /**
   * Explicit advance, wired to each section's own Continue button: collapses
   * the step and expands the one after it. Returns the newly-opened step (or
   * null at the end) so the caller can move focus to it. This replaced
   * blur-driven auto-advance — nothing ever collapses except on Continue, so
   * a section can no longer close under someone who is still editing it.
   */
  advance: (step: StepId) => StepId | null;
};

const INITIAL_STATUS: StepStatusMap = {
  info: 'active',
  shipping: 'active',
  billing: 'active'
};

function nextStepOf(step: StepId): StepId | undefined {
  return APPLICATION_STEPS[APPLICATION_STEPS.indexOf(step) + 1];
}

/** Reads a valid step id from the URL hash (e.g. `#billing`), or null. */
function readHashStep(): StepId | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = window.location.hash.replace(/^#/, '');
  return (APPLICATION_STEPS as string[]).includes(raw) ? (raw as StepId) : null;
}

/**
 * Step state machine for the single-page application.
 *
 * Nothing is ever locked: any section opens at any time, in any order. Only
 * `advance` — wired to each section's Continue — ever collapses one, so a
 * section can never close while someone is still typing in it.
 *
 * The open step is mirrored to the URL hash (`/apply#billing`) so refresh and
 * the back button land where the member was.
 */
export function useApplicationSteps(): ApplicationStepsTools {
  const [status, setStatus] = useState<StepStatusMap>(INITIAL_STATUS);
  const [openSteps, setOpenSteps] = useState<Record<StepId, boolean>>({
    info: true,
    shipping: false,
    billing: false
  });

  // Apply a hash deep-link once, on mount — in an effect, not initial state,
  // so the server render and the first client render agree (no hydration
  // mismatch when the URL carries `#shipping`/`#billing`).
  useEffect(() => {
    const step = readHashStep();
    if (step && step !== 'info') {
      setOpenSteps({
        info: false,
        shipping: false,
        billing: false,
        [step]: true
      });
    }
  }, []);

  const isOpen = useCallback((step: StepId) => !!openSteps[step], [openSteps]);

  // Two sections open at once means two Continue buttons, and the page shows
  // one primary action at a time.
  const requestOpen = useCallback((step: StepId, open: boolean) => {
    setOpenSteps(() => ({
      info: false,
      shipping: false,
      billing: false,
      [step]: open
    }));
  }, []);

  const reportComplete = useCallback((step: StepId, complete: boolean) => {
    setStatus((current) => {
      const next: StepStatus = complete ? 'complete' : 'active';
      return current[step] === next ? current : { ...current, [step]: next };
    });
  }, []);

  const advance = useCallback((step: StepId): StepId | null => {
    const next = nextStepOf(step) ?? null;

    setOpenSteps((current) => ({
      ...current,
      [step]: false,
      ...(next ? { [next]: true } : {})
    }));

    return next;
  }, []);

  // Mirror the primary open step to the URL hash (replace, not push — no
  // history spam). "Primary" = the furthest-along open step.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const primary = [...APPLICATION_STEPS].reverse().find((s) => openSteps[s]);
    if (primary && window.location.hash !== `#${primary}`) {
      window.history.replaceState(null, '', `#${primary}`);
    }
  }, [openSteps]);

  const anyOpen = APPLICATION_STEPS.some((step) => openSteps[step]);

  return useMemo(
    () => ({ status, isOpen, requestOpen, reportComplete, advance, anyOpen }),
    [status, isOpen, requestOpen, reportComplete, advance, anyOpen]
  );
}

export default useApplicationSteps;
